import { useAppDispatch } from 'app/hooks';
import store from 'app/store';
import { EquipmentDragOverride } from 'components/structure-page/3d/equipment-gizmo-3d';
import {
  FootprintPolygonsType,
  GridObjectType,
  GridSpaceDataType,
  GridSpaceType,
  LevelType
} from 'data/types';
import {
  DirectPointDragHandlers,
  useDirectPointDrag
} from 'hooks/use-direct-point-drag';
import { useMemo, useRef } from 'react';
import { SCALE } from 'utils/3d';
import { getEquipment3DPlacement } from 'utils/3d-equipment';
import {
  moveAirHandlerToGridPosition,
  moveCondenserToGridPosition
} from 'utils/equipment-move';
import { beginLinesetDrag, endLinesetDrag } from 'utils/lineset-drag-state';

type UseEquipmentDirectDragArgs = {
  object: GridObjectType;
  spaces: GridSpaceType[];
  footprints: FootprintPolygonsType;
  structure: LevelType[];
  levelIndex: number;
  yOffset: number;
  configurationId?: string;
  gridSpaces: GridSpaceDataType[];
  isBlocked?: () => boolean;
  onDragOverrideChange?: (override: EquipmentDragOverride | null) => void;
};

export function useEquipmentDirectDrag({
  object,
  spaces,
  footprints,
  structure,
  levelIndex,
  yOffset,
  configurationId,
  gridSpaces,
  isBlocked,
  onDragOverrideChange
}: UseEquipmentDirectDragArgs): DirectPointDragHandlers {
  const dispatch = useAppDispatch();
  const objectRef = useRef(object);
  const dragStartGridRef = useRef({ x: 0, y: 0 });
  const dragStartAnchorRef = useRef({ x: 0, z: 0 });
  const lastGridRef = useRef({ x: 0, y: 0 });

  objectRef.current = object;

  const placement = useMemo(
    () => getEquipment3DPlacement(object, spaces, footprints),
    [object, spaces, footprints]
  );
  const placementRef = useRef(placement);
  placementRef.current = placement;

  const dragId = `equipment-3d-${object.id}`;

  return useDirectPointDrag({
    pointPosition: [placement.x, placement.y + yOffset, placement.z],
    isBlocked,
    onDragStart: () => {
      const startGrid = {
        x: objectRef.current.position.x,
        y: objectRef.current.position.y
      };
      dragStartGridRef.current = startGrid;
      lastGridRef.current = startGrid;
      dragStartAnchorRef.current = {
        x: placementRef.current.x,
        z: placementRef.current.z
      };
      beginLinesetDrag(dragId);
    },
    onDragChange: (localPosition) => {
      const deltaGridX = Math.round(
        (localPosition.x - dragStartAnchorRef.current.x) / SCALE
      );
      const deltaGridY = Math.round(
        (localPosition.z - dragStartAnchorRef.current.z) / SCALE
      );
      const gridX = Math.max(0, dragStartGridRef.current.x + deltaGridX);
      const gridY = Math.max(0, dragStartGridRef.current.y + deltaGridY);
      lastGridRef.current = { x: gridX, y: gridY };
      onDragOverrideChange?.({
        objectId: objectRef.current.id,
        x: gridX,
        y: gridY
      });
    },
    onDragEnd: (localPosition) => {
      const startGrid = dragStartGridRef.current;
      const finalGrid = lastGridRef.current;
      const moved =
        localPosition !== null &&
        (finalGrid.x !== startGrid.x || finalGrid.y !== startGrid.y);

      if (moved && configurationId) {
        const currentObject = objectRef.current;
        const { configurations } = store.getState().configurations;
        if (currentObject.type === 'condenser') {
          moveCondenserToGridPosition({
            dispatch,
            configurations,
            configurationUniqueId: configurationId,
            condenserUniqueId: currentObject.itemId,
            x: finalGrid.x,
            y: finalGrid.y,
            levels: structure,
            levelIndex,
            gridSpaces
          });
        } else {
          moveAirHandlerToGridPosition({
            dispatch,
            configurations,
            configurationUniqueId: configurationId,
            zoneItemId: currentObject.id.replace('-air-handler', ''),
            x: finalGrid.x,
            y: finalGrid.y,
            gridSpaces
          });
        }
      }

      onDragOverrideChange?.(null);
      setTimeout(() => {
        endLinesetDrag(dragId);
      }, 0);
    }
  });
}
